import axios from 'axios';
import { baseURL } from '../settings';

export const get = path =>
  axios
    .get(`${baseURL}${path}`)
    .then(res => {
      return res;
    })
    .catch(error => error);

export const put = (path, payload, headers) =>
  axios({
    url: `${baseURL}${path}`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    data: payload
  })

export const post = (path, payload) =>
  axios({
    url: `${baseURL}${path}`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    data: payload
  })
    .then(res => {
      return res;
    })
    .catch(error => {
      return Promise.reject(error)
    });

export const getById = (path, payload) => {
  return axios
    .get(`${baseURL}${path}/${payload}`)
    .then(res => {
      return res;
    })
    .catch(error => {
      throw new Error(error);
    });
};

export const del = (path) => {
    return axios
        .delete(`${baseURL}${path}`)
        .then(res => {
            return res;
        })
        .catch(error => {
            throw new Error(error);
        });
};

// Alias for backward compatibility
export const deleteSpace = del;

/**
 * Offline Booking API Functions
 * Integrate with backend offline booking endpoints
 * Refactored to use axios interceptor for automatic auth token handling
 */

// Create offline booking
export const createOfflineBooking = (bookingData) =>
  axios
    .post(`${baseURL}api/booking/`, {
      ...bookingData,
      bookingType: 'OFFLINE_BOOKING'
    })
    .then(res => res.data)
    .catch(error => {
      console.error('Error creating offline booking:', error);
      throw error.response?.data || error.message;
    });

// Create payment for offline booking
export const createOfflineBookingPayment = (paymentData) =>
  axios
    .post(`${baseURL}api/pay/offline-booking`, paymentData)
    .then(res => {
      console.log('API: Payment created, response:', res);
      return res.data;
    })
    .catch(error => {
      console.error('API: Error creating offline booking payment:', error);
      throw error;
    });

// Update offline booking payment
export const updateOfflineBookingPayment = (paymentId, updateData) =>
  axios
    .put(`${baseURL}api/pay/offline-booking/${paymentId}`, updateData)
    .then(res => res.data)
    .catch(error => {
      console.error('Error updating offline booking payment:', error);
      throw error.response?.data || error.message;
    });

// Get offline booking payment
export const getOfflineBookingPayment = (bookingId) =>
  axios
    .get(`${baseURL}api/pay/offline-booking/${bookingId}`)
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching offline booking payment:', error);
      throw error.response?.data || error.message;
    });

// Fetch offline bookings by booking type (Admin only)
export const fetchOfflineBookingsByType = (bookingType = 'OFFLINE_BOOKING', page = 0, size = 100) =>
  axios
    .get(`${baseURL}api/booking/byBookingType`, {
      params: {
        bookingType,
        page,
        size
      }
    })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching bookings by type:', error);
      throw error.response?.data || error.message;
    });

// Update booking status (with proper version handling for optimistic locking)
export const updateBookingStatus = async (bookingId, bookingStatus) => {
  try {
    // First, fetch the booking to get current version
    const bookingResponse = await axios.get(`${baseURL}api/booking/${bookingId}`);
    const booking = bookingResponse.data;
    
    if (!booking) {
      throw new Error('Booking not found');
    }
    
    // Get version from ETag header or booking data
    const version = bookingResponse.headers.etag || booking.version;
    
    console.log('Updating booking status - ID:', bookingId, 'Status:', bookingStatus, 'Version:', version);
    
    // Update the booking with If-Match header for optimistic locking
    const updateResponse = await axios.put(
      `${baseURL}api/booking/`,
      {
        ...booking,
        bookingStatus: bookingStatus
      },
      {
        headers: {
          'If-Match': version
        }
      }
    );
    
    return updateResponse.data;
  } catch (error) {
    console.error('Error updating booking status:', error);
    console.error('Error response:', error.response);
    throw error.response?.data || error.message;
  }
};

// Cancel booking - special endpoint that allows cancellation even with completed payment
export const cancelBooking = async (bookingId) => {
  try {
    // First, fetch the booking to get current version
    const bookingResponse = await axios.get(`${baseURL}api/booking/${bookingId}`);
    const booking = bookingResponse.data;
    
    if (!booking) {
      throw new Error('Booking not found');
    }
    
    // Get version from ETag header or booking data
    const version = bookingResponse.headers.etag || booking.version;
    
    console.log('Cancelling booking - ID:', bookingId, 'Version:', version);
    
    // Update the booking with If-Match header for optimistic locking
    // Set bookingStatus to CANCELLED - backend allows this even with completed payment
    const updateResponse = await axios.put(
      `${baseURL}api/booking/`,
      {
        ...booking,
        bookingStatus: 'CANCELLED'
      },
      {
        headers: {
          'If-Match': version,
          'X-Force-Cancel': 'true' // Add special header to indicate this is a cancellation
        }
      }
    );
    
    return updateResponse.data;
  } catch (error) {
    console.error('Error cancelling booking:', error);
    console.error('Error response:', error.response);
    
    // Provide a clearer error message
    const errorMsg = error.response?.data?.message || error.message || 'Failed to cancel booking';
    throw new Error(errorMsg);
  }
};

// Fetch booking availability
export const fetchBookingAvailability = (spaceId, eventTypeId, startDate, endDate, chargingType) =>
  axios
    .get(`${baseURL}api/booking/availability`, {
      params: {
        space: spaceId,
        eventTypeId,
        startDate,
        endDate,
        chargingType
      }
    })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching booking availability:', error);
      throw error.response?.data || error.message;
    });

// Fetch bookings by booking type
export const getBookingsByType = (bookingType, page = 0, size = 100) =>
  axios
    .get(`${baseURL}api/booking/byBookingType`, {
      params: {
        bookingType,
        page,
        size
      }
    })
    .then(res => res.data)
    .catch(error => {
      console.error('Error fetching bookings by type:', error);
      throw error.response?.data || error.message;
    });

